import { createRoot } from "react-dom/client";
import { toPng } from "html-to-image";
import P3ChartSection from "../features/charts/p3/P3ChartSection";
import P5ChartSection from "../features/charts/p5/P5ChartSection";

const PREGUNTA_IDS = ["p3", "p5"] as const;
const MAX_WAIT_MS = 30_000;
const POLL_INTERVAL_MS = 500;
const INITIAL_DELAY_MS = 1_000;
const SETTLE_DELAY_MS = 500;

export type ChartImages = Record<string, string[]>;

function AllChartsRenderer() {
  return (
    <>
      <div data-pregunta="p3"><P3ChartSection /></div>
      <div data-pregunta="p5"><P5ChartSection /></div>
    </>
  );
}

function waitForChartsToRender(container: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    let elapsed = 0;

    const check = () => {
      const cards = container.querySelectorAll("[data-chart-card]");
      const loading = container.querySelectorAll("[data-chart-card] .ant-spin");

      if (cards.length > 0 && loading.length === 0) {
        setTimeout(resolve, SETTLE_DELAY_MS);
        return;
      }

      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= MAX_WAIT_MS) {
        resolve();
        return;
      }

      setTimeout(check, POLL_INTERVAL_MS);
    };

    setTimeout(check, INITIAL_DELAY_MS);
  });
}

export async function captureAllCharts(): Promise<ChartImages> {
  const container = document.createElement("div");
  Object.assign(container.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: "800px",
    zIndex: "-1",
    background: "#fff",
  });
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(<AllChartsRenderer />);

  await waitForChartsToRender(container);

  const images: ChartImages = {};

  for (const pid of PREGUNTA_IDS) {
    const wrapper = container.querySelector(`[data-pregunta="${pid}"]`);
    if (!wrapper) continue;

    const chartCards = Array.from(wrapper.querySelectorAll<HTMLElement>("[data-chart-card]"));
    if (chartCards.length === 0) continue;

    images[pid] = [];
    for (const card of chartCards) {
      try {
        const dataUrl = await toPng(card, {
          pixelRatio: 2,
          backgroundColor: "#ffffff",
        });
        images[pid].push(dataUrl);
      } catch (err) {
        console.warn(`Failed to capture chart for ${pid}:`, err);
      }
    }
  }

  root.unmount();
  document.body.removeChild(container);

  return images;
}
